import { useState } from 'react'
import { api, ErroreApi } from '../servizi/api.ts'
import { Avviso } from '../componenti/Pezzi.tsx'
import { Bottone, Spunta } from '../componenti/base.tsx'
import { CampoCasellaCidis } from './CampoCasellaCidis.tsx'

/**
 * Amministrazione > Utenti > Modifica di un collaboratore esterno: le deroghe (533, decisioni di Davide
 * del 2/11/2026).
 *
 * Il collaboratore entra con un indirizzo @esterni.cidisonlus.org e la sua casella personale, su quel
 * dominio, per difetto non riceve da fuori CIDIS e non spedisce fuori: serve alle comunicazioni interne.
 * Chi gestisce le utenze concede qui, una per una, le deroghe:
 *  - «Riceve da fuori» e «Invia fuori», i due interruttori della casella;
 *  - la casella cidisonlus.org, che nasce casella di gruppo con la persona come membro che scrive (la
 *    trova in «Posta» accanto alla sua). Si entra sempre con l'indirizzo @esterni.
 * Come le caselle di gruppo, valgono subito, senza «Salva», e ogni passo resta nel registro.
 */

export type CasellaPersonale = {
  id: string; indirizzo: string; modalita: string; attiva: boolean; riceve_esterno: boolean; invia_esterno: boolean
}

const testo = (e: unknown) =>
  (e instanceof ErroreApi ? e.message : 'Non è andata a buon fine: controlla la connessione e riprova.')

export function DerogheEsterno({ utenteId, nome, cognome, casella, creata }: {
  utenteId: string
  nome: string
  cognome: string
  casella: CasellaPersonale | null
  /** Dopo la casella cidisonlus.org: l'elenco delle caselle condivise si ricarica. */
  creata: () => void
}) {
  const [stato, setStato] = useState(casella)
  const [errore, setErrore] = useState<string | null>(null)
  const [messaggio, setMessaggio] = useState<string | null>(null)
  const [inCorso, setInCorso] = useState<string | null>(null)
  const [apriCidis, setApriCidis] = useState(false)
  const [indirizzo, setIndirizzo] = useState('')
  const [libero, setLibero] = useState<boolean | null>(null)

  async function interruttore(campo: 'riceve_esterno' | 'invia_esterno', valore: boolean) {
    setInCorso(campo); setErrore(null); setMessaggio(null)
    try {
      const r = await api.put<{ riceve_esterno: boolean; invia_esterno: boolean }>(
        `/amministrazione/utenti/${utenteId}/casella/verso-esterno`, { [campo]: valore })
      setStato((s) => (s ? { ...s, ...r } : s))
      setMessaggio(campo === 'riceve_esterno'
        ? (valore ? 'Ricezione da fuori accesa: la casella riceve anche da fuori CIDIS entro cinque minuti.'
          : 'Ricezione da fuori spenta: chi scrive da fuori CIDIS riceve un rifiuto.')
        : (valore ? 'Invio fuori acceso.' : 'Invio fuori spento: la casella scrive solo alle caselle di CIDIS.'))
    } catch (e) { setErrore(testo(e)) }
    finally { setInCorso(null) }
  }

  async function creaCidis() {
    setInCorso('cidis'); setErrore(null); setMessaggio(null)
    try {
      const r = await api.post<{ id: string; indirizzo: string }>(`/amministrazione/utenti/${utenteId}/casella-cidis`,
        { indirizzo: indirizzo || null })
      setMessaggio(`Creata ${r.indirizzo}: la persona la trova in «Posta» accanto alla sua. Si entra sempre con l’indirizzo @esterni.`)
      setApriCidis(false); setIndirizzo(''); setLibero(null)
      creata()
    } catch (e) { setErrore(testo(e)) }
    finally { setInCorso(null) }
  }

  return (
    <section className="operatori-sezione">
      <h3 className="operatori-titoletto">Collaboratore esterno: deroghe</h3>
      <p className="piccolo tenue">
        Entra con l’indirizzo @esterni.cidisonlus.org. La sua casella serve alle comunicazioni interne: per
        difetto non riceve da fuori CIDIS e non spedisce fuori. Le deroghe valgono subito, senza «Salva», e
        restano nel registro.
      </p>
      {errore && <Avviso tipo="errore">{errore}</Avviso>}
      {messaggio && <Avviso tipo="successo">{messaggio}</Avviso>}
      {!stato
        ? <p className="piccolo tenue">Non ha ancora una casella personale.</p>
        : stato.modalita !== 'ospitata'
          ? <p className="piccolo tenue">La casella {stato.indirizzo} non è ospitata dal gestionale.</p>
          : (
            <>
              <p className="piccolo">Casella <span className="mono">{stato.indirizzo}</span>
                {!stato.attiva && <span className="tenue"> · disattivata</span>}</p>
              <Spunta etichetta="Riceve da fuori" valore={stato.riceve_esterno} disabilitato={inCorso !== null}
                      cambia={(v) => void interruttore('riceve_esterno', v)}
                      aiuto={stato.riceve_esterno
                        ? 'Riceve anche da indirizzi fuori da CIDIS.'
                        : 'Riceve solo dalle caselle di CIDIS e dal gestionale; chi scrive da fuori riceve un rifiuto.'} />
              <Spunta etichetta="Invia fuori" valore={stato.invia_esterno} disabilitato={inCorso !== null}
                      cambia={(v) => void interruttore('invia_esterno', v)}
                      aiuto={stato.invia_esterno
                        ? 'Scrive anche a indirizzi fuori da CIDIS.'
                        : 'Scrive solo alle caselle di CIDIS: un destinatario fuori si segnala prima dell’invio.'} />
            </>
          )}
      <div className="operatori-sezione">
        {!apriCidis
          ? (
            <Bottone dimensione="piccolo" onClick={() => setApriCidis(true)}>
              Crea anche la casella cidisonlus.org
            </Bottone>
          )
          : (
            <>
              <div className="griglia-modulo">
                <CampoCasellaCidis nome={nome} cognome={cognome} valore={indirizzo}
                                   etichetta="Indirizzo della casella cidisonlus.org"
                                   cambia={(p) => {
                                     if (p.indirizzo_cidis !== undefined) setIndirizzo(p.indirizzo_cidis)
                                     if (p.indirizzo_libero !== undefined) setLibero(p.indirizzo_libero)
                                   }} />
              </div>
              <p className="piccolo tenue">
                Nasce come casella condivisa con questa persona sola, che legge e scrive; si possono aggiungere
                altri. Non diventa un accesso.
              </p>
              <div className="riga">
                <Bottone variante="primario" dimensione="piccolo" disabled={libero !== true} inCorso={inCorso === 'cidis'}
                         onClick={() => void creaCidis()}>
                  Crea la casella
                </Bottone>
                <Bottone variante="discreto" dimensione="piccolo" onClick={() => { setApriCidis(false); setIndirizzo('') }}>
                  Annulla
                </Bottone>
              </div>
            </>
          )}
      </div>
    </section>
  )
}
